import React, { useState, useRef, useEffect } from 'react';
// eslint-disable-next-line no-unused-vars
import { TextField, Box, InputBase, InputAdornment,FormHelperText } from '@mui/material';
import { FixedSizeList } from 'react-window';
import MenuItem from '@mui/material/MenuItem';
import PropTypes from 'prop-types';
import useTheme from '../../hooks/useTheme';
import {
	ArrowDownIcon,
	CheckIcon,
	ArrowUpIcon,
	AddIcon3,
	AddIcon2,
} from '../../assets/icons/index';
import Label from '../label/Label';
import { Typography } from '@mui/material';
import Tooltip from '../tooltip/Tooltip';

const CustomSelectField = ({
	id,
	error,
	label,
	width,
	selectedItem,
	optionItem,
	onChangeText,
	dataTestId = 'custom-selecttextfield',
	isSearchOptionRequiredOrNot,
	sx = {},
	disabled = false,
	required = true,
	height = '48px',
	tooltipText,
	tooltipWidth,
	onAddClick,
	disableOptions = false,
	addOption,
	newAddOption,
	newAddOptionLabel,
	isSearchOptionRequired = false,
	onSearch = () => {},
	form,
	readOnly= false,
	preventUnselect = false,
}) => {
	const selectedOption =
		optionItem?.length > 0 &&
		optionItem.find((option) => option?.value === selectedItem);
	const ROW_HEIGHT = 33;
	const MAX_VISIBLE_ITEMS = 6;
	const [menuOpen, setMenuOpen] = React.useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const selectRef = useRef(null);
	const [isFocused, setIsFocused] = useState(false);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (selectRef.current && !selectRef.current.contains(event.target)) {
				setIsFocused(false);
				selectRef.current.querySelector('input')?.blur(); // Blur the input field
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleMenuOpen = (event) => {
		if (disableOptions) return;
		setMenuOpen(true);
	};

	const handleMenuClose = () => {
		setMenuOpen(false);
	};

	const onChangeLabel = (event) => {
		setSearchTerm(event?.target?.value);
		onSearch(event);
	};

	const { FontFace, color } = useTheme();

	const handleChange = (event) => {
		const selectedValue = event?.target?.value;

		if (selectedValue === 'add') {
			onAddClick();
		} else if (selectedValue === selectedItem) {
			if (preventUnselect) {
				// do nothing → keep selection
				setMenuOpen(false);
				return;
			  }
			onChangeText({ target: { value: '' } });
			setMenuOpen(true);
		} else {
			onChangeText(event);
			setMenuOpen(false);
		}
	};
	

	const filteredOptions = optionItem
		?.filter((option) => option?.label != null) // Remove null or undefined labels
		?.filter((option) =>
			String(option.label)
				.toLowerCase()
				.includes(searchTerm?.toLowerCase())
		);
	const visibleCount = Math.min(filteredOptions?.length ?? 0, MAX_VISIBLE_ITEMS);
	const listHeight = Math.max(visibleCount * ROW_HEIGHT, 0);

	const renderRow = ({ index, style }) => {
		const option = filteredOptions[index];
		const handleItemClick = () => {
			handleChange({ target: { value: option?.value } });
		};
		return (
			<MenuItem
				key={option?.value}
				value={option?.value}
				disabled={disabled}
				onClick={handleItemClick}
				style={{
					backgroundColor:
						selectedItem === option?.value
							? color.background.brand_outline
							: color.background.primary,
					width: '100%',
					overflow:'hidden',
					...style,
				}}
				sx={{
					'& .MuiTouchRipple-root .MuiTouchRipple-ripple': {
						color: color.button.primary,
					},
				}}
			>
				{option?.tooltipText ? (
					<Tooltip
						title={option.tooltipText}
						componentsProps={{
							tooltip: {
								sx: {
									bottom: '-4px',
									bgcolor: '#ffffff',
									color: '#000000',
									border: '1px solid rgba(0, 0, 0, 0.5)',
									borderRadius: '4px',
									padding: '8px',
									boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.16)',
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									alignItems: 'center',
									width: option.tooltipWidth,
								},
							},
						}}
						leaveDelay={0} // Removes delay when hiding the tooltip
						disableInteractive
					>
						{menuItemContent(option)}
					</Tooltip>
				) : (
					menuItemContent(option)
				)}
			</MenuItem>
		);
	};

	const onClickCapture = (e) => {
		if (!e.target.closest('.add-button-container')) {
			e.stopPropagation();
			e.preventDefault();
		}
	};

	const SearchBar = () => (
		<div
			onClickCapture={onClickCapture}
			onKeyDown={(e) => e.stopPropagation()}
			onMouseEnter={(e) => e.preventDefault()}
			style={{
				zIndex: 100,
				backgroundColor: color.background.primary,
				position: 'sticky',
				top: -8,
			}}
		>
			<InputBase
				placeholder={newAddOptionLabel || 'Search'}
				value={searchTerm}
				onChange={onChangeLabel}
				autoFocus={true}
				onKeyDown={(event) => {
					if (
						event.key === 'a' ||
						event.key === 's' ||
						event.key === 'A' ||
						event.key === 'S'
					) {
						event.stopPropagation();
					}
				}}
				style={{
					width: '100%', // Ensures the input spans the entire width
					marginBottom: '8px',
					fontSize: FontFace.font_size.xx_inputlabel,
					padding: '8px 0px 8px 8px', // Add padding to ensure text is not cramped
					fontFamily: 'Inter-Regular',
					lineHeight: '1.33',
					borderBottom: `1px solid ${color.input.line}`, // Customize the border
				}}
				sx={{
					'& input::placeholder': {
						fontSize: FontFace.font_size.xx_inputlabel,
						color: color.text.primary,
					},
					'&.Mui-focused': {
						borderBottomColor: color.input.line_active,
					},
					'& input': {
						fontSize: FontFace.font_size.xx_inputlabel,
						color: color.text.primary,
					},
					'& .MuiInputAdornment-root': {
						marginRight: '10px', // Ensure there's space for the add button
					},
				}}
				endAdornment={
					newAddOption && (
						<InputAdornment position="end">
							<Box
								sx={{
									display: 'flex',
									marginRight: '-10px',
									marginBottom: '4px',
									justifyContent: 'center',
									alignItems: 'center',
									// width: '48px',
									height: '17px',
									padding: '4px',
									cursor: 'pointer',
									'&:hover': {
										cursor: 'pointer',
										backgroundColor: color.background.tertiary,
									},
								}}
								className="add-button-container"
								onClick={onAddClick}
							>
								<Box
									sx={{
										display: 'flex',
										cursor: 'pointer',
										alignItems: 'center',
									}}
								>
									<Box
										style={{
											cursor: 'pointer',
											// margingRight: '4px',
										}}
									>
										<Label
											label={'Add'}
											type={'secondary'}
											fontFamily={'Inter-Bold'}
											fontSize={'input_text'}
											style={{
												cursor: 'pointer',
											}}
										/>
									</Box>
									<AddIcon3 width="16px" height="16px" />
								</Box>
							</Box>
						</InputAdornment>
					)
				}
			/>
		</div>
	);

	const menuItemContent = (option) => {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					width: '100%',
					cursor: 'pointer',
				}}
			>
				<Box sx={{ 
					flex: 1, 
					minWidth: 0, 
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
				}}>
					<Label
						label={option.label}
						type={'primary'}
						fontFamily={'Inter-Regular'}
						fontSize={'input_text'}
					/>
				</Box>
				{selectedItem === option?.value && (
					<CheckIcon width={'16px'} height={'16px'} />
				)}
			</Box>
		);
	};

	const textFieldComponent = (
		<div ref={selectRef} style={sx}>
			<TextField
				required={required}
				disabled={disabled}
				select
				type="text"
				id={id}
				error={!!error}
				label={label}
				helperText={error}
				FormHelperTextProps={{
					style: {
						fontFamily: 'Inter-Regular',
						color: color.text.error,
						fontSize: FontFace.font_size.xx_small,
					},
				}}
				InputLabelProps={{
					style: {
						fontFamily: 'Inter-Regular',
						fontSize: selectedOption
									? FontFace.font_size.xx_medium
									: FontFace.font_size.input_text,
						color: color.text.secondary,
					},
				}}
				data-testid={dataTestId}
				InputProps={{ inputProps: { id } }}
				variant="standard"
				sx={{
					minWidth: width,
					maxWidth: width,
					height: height,
					...(readOnly && {
						pointerEvents: "none",
						"& .MuiInput-underline:after": {
							borderBottom: "none", // Prevents the focus effect (purple underline)
						},
					}),
					'& .MuiInputBase-input': {
						// color: color.text.secondary,
						fontSize:
							form === 'dynamicForm'
								? FontFace.font_size.x_small
								: FontFace.font_size.input_text,
						fontFamily: 'Inter-Regular, sans-serif',
						color: '#121212',
						cursor: disabled ? 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\'><text x=\'0\' y=\'20\' font-size=\'20\' fill=\'red\'>🚫</text></svg>"), not-allowed' : '',
					},
					'& .MuiInputLabel-root': {
						color: color.text.secondary,
						fontFamily: 'Inter-Medium, sans-serif',
						fontSize: FontFace.font_size.large,
					},
					fontFamily: 'Inter-Medium, sans-serif',
					'& label': {
						fontFamily: 'Inter-Regular, sans-serif',
						fontSize: FontFace.font_size.input_text,
					},
					'& input': {
						fontFamily: 'Inter-Regular, sans-serif',
						fontSize: FontFace.font_size.input_text,
					},
					'& label.Mui-focused': {
						color: color.text.secondary,
						fontSize: FontFace.font_size.large,
					},
					'& .MuiInput-underline:before': {
						borderBottomColor: color.input.line,
					},
					'& .MuiInput-underline:after': {
						borderBottomColor: color.input.line_active,
						borderBottomWidth: '2px',
						backgroundColor: color.input.background_active,
					},
					'& .MuiInput-underline:hover:not(.Mui-disabled):before': {
						borderBottomColor: color.input.line_active,
						borderBottomWidth: '2px',
					},
					'& input:-webkit-autofill': {
						WebkitBoxShadow: '0 0 0px 1000px white inset',
					},
					'& .MuiMenuItem-root': {
						fontFamily: 'Inter-Regular',
					},
					'& .MuiTouchRipple-root': {
						color: color.button.primary,
					},
					'& .MuiInput-input:focus': {
						backgroundColor: color.background.primary,
					},
					// Styling the arrow based on hover and focus
					'&:hover .dropdown-icon path, &:focus-within .dropdown-icon path': {
						stroke: '#545454', // Change icon color on hover and focus
					},
					...sx,
				}}
				size="small"
				margin="none"
				value={selectedOption ? selectedOption.label : ''}
				onChange={(e) => !disabled && handleChange(e)}
				SelectProps={{
					open: menuOpen || isFocused,
					renderValue: (selected) =>
						selectedOption ? selectedOption.label : '',
					IconComponent: (props) => (
						<div
							onClick={(e) => {
								if (disableOptions) return;
								setMenuOpen(!menuOpen);
								e.stopPropagation();
								e.currentTarget.parentElement.click();
							}}
							style={{
								cursor: disableOptions ? 'not-allowed' : 'pointer',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							{menuOpen && (
								<ArrowUpIcon
									stroke={color.icon.brand}
									className="dropdown-icon"
									width={'24px'}
									height={'24px'}
									style={{
										marginBottom: '4px',
										marginRight: '6px',
									}}
								/>
							)}
							{(!menuOpen && !disabled) &&(
								<ArrowDownIcon
									className="dropdown-icon"
									width={'24px'}
									height={'24px'}
									style={{
										marginBottom: '4px',
										marginRight: '6px',
									}}
								/>
							)}
						</div>
					),
					onOpen: () => handleMenuOpen(),
					onClose: () => handleMenuClose(),
					MenuProps: {
						PaperProps: {
							style: {
								// maxHeight: 280,
								width: width ? `${width}px` : 'auto',
								border: `solid 1px ${color.stroke.light}`,
								boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.16)',
								overflow: 'visible',
								maxHeight: 'unset',
							},
						},
						MenuListProps: {
							sx: {
								// maxHeight: Math.min(ROW_HEIGHT * (filteredOptions?.length || 0) + 16, 220), // Dynamic height based on items
								overflowY: 'visible',
								overflowX: 'hidden',
								marginTop: '8px',
								backgroundColor: color.background.primary,
								maxHeight: 'unset',
							},
						},
					},
				}}
			>
				{/* {isSearchOptionRequired && <SearchBar />} */}
				{newAddOption && (isSearchOptionRequired === false && isSearchOptionRequiredOrNot === false) && (
					<Box>
						<Box
							sx={{
								display: 'flex',
								// paddingTop: '5px',
								padding: '5px 12px',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<Typography
								fontSize="12px"
								fontFamily={'Inter-Regular'}
								sx={{ color: '#545454', marginBottom: '4px' }}
							>
								{newAddOptionLabel}
							</Typography>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									cursor: 'pointer',
								}}
								onClick={onAddClick}
							>
								<Typography
									color="#545454"
									fontSize="14px"
									fontFamily={'Inter-Regular'}
									sx={{
										marginRight: '4px',
										fontWeight: '500',
									}}
								>
									Add
								</Typography>
								<AddIcon2
									style={{
										width: '16px',
										height: '16px',
										color: '#545454',
									}}
								/>
							</Box>
						</Box>
						<Box
							sx={{
								width: '100%',
								height: '1px',
								backgroundColor: '#ddd',
							}}
						/>
					</Box>
				)}
				{(isSearchOptionRequiredOrNot || isSearchOptionRequired) && <SearchBar />}
				{filteredOptions?.length > 0 && (
					<FixedSizeList
						height={listHeight}
						itemCount={filteredOptions.length}
						itemSize={ROW_HEIGHT}
						itemKey={(index) => filteredOptions[index]?.value ?? index}
					>
						{renderRow}
					</FixedSizeList>
				)}
				{addOption && (
					<MenuItem value="add">+ Add</MenuItem> // Conditionally render the "+ Add" option
				)}
				{optionItem?.length === 0 && (
					<MenuItem
						key={'defaultValue'}
						value={''}
						disabled={true}
						style={{
							backgroundColor: color.background.primary,
							color: color.text.secondary,
							opacity: 1,
							cursor: 'not-allowed',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								width: '100%',
							}}
						>
							<Label
								type={'disabled'}
								fontSize={'x_small'}
								fontFamily={'Inter-Regular'}
								label={'No Option Available'}
							/>
						</Box>
					</MenuItem>
				)}
				{filteredOptions?.length == 0 && searchTerm && (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							height: '50px',
							alignItems: 'center',
						}}
					>
						<Label
							label={'No option available'}
							// type={'primary'}
							fontFamily={'Inter-Regular'}
							fontSize={'input_text'}
							type={'disabled'}
						/>
					</div>
				)}
			</TextField>
			{/* {error && (
				<FormHelperText
					sx={{
						fontSize: '12px',
						color: color.text.error,
						fontFamily: 'Inter-Regular',
						marginTop: '4px',
					}}
				>
					{error}
				</FormHelperText>
			)} */}
		</div>
	);

	return tooltipText ? (
		<Tooltip
			content={tooltipText}
			type="basic"
			direction="bottom"
			delay={300}
			style={{ width: tooltipWidth || 'auto' }}
			hideTooltip={selectedOption?.label ? true : false}
		>
			{textFieldComponent}
		</Tooltip>
	) : (
		textFieldComponent
	);
};

CustomSelectField.propTypes = {
	id: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	width: PropTypes.number.isRequired,
	optionItem: PropTypes.array.isRequired,
	onChangeText: PropTypes.func.isRequired,
	dataTestId: PropTypes.string.isRequired,
	error: PropTypes.string,
	selectedItem: PropTypes.string,
	sx: PropTypes.object,
	disabled: PropTypes.bool,
	required: PropTypes.bool,
	height: PropTypes.string,
	tooltipText: PropTypes.string,
	tooltipWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // Added tooltipWidth prop type
	onAddClick: PropTypes.func,
	addOption: PropTypes.bool,
	newAddOption: PropTypes.bool,
	newAddOptionLabel: PropTypes.string,
	isSearchOptionRequired: PropTypes.bool,
	onSearch: PropTypes.func,
	form: PropTypes.string,
	isSearchOptionRequiredOrNot: PropTypes.bool,
	disableOptions: PropTypes.bool,
	readOnly: PropTypes.bool,
	preventUnselect: PropTypes.bool
};

export default CustomSelectField;